// The object-driven sidebar interpreter. Renders any PanelConfig into a panel
// using the shared primitives — zero business logic, every behaviour comes from
// the declarative vocabulary in @clarix/shared-types, resolved against the
// Clarix stack (react-router + @tanstack/react-query + @clarix/rbac).
//
// Mount as <DeclarativePanel key={panelId} …> so switching panels remounts and
// the useQueries array length stays stable per mounted panel (hooks-safe).

import { useMemo, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, ExternalLink, Pin, Plus } from 'lucide-react';
import { cn, Skeleton } from '@clarix/ui';
import { DynamicIcon, HighlightText } from '@clarix/ui-shared';
import { usePermission, usePermissionsSlice } from '@clarix/rbac';
import type {
  BadgeConfig,
  BadgeTone,
  PanelConfig,
  RowConfig,
  RowMapping,
  SectionConfig,
  SidebarProviderResult,
  SidebarSectionsResult,
} from '@clarix/shared-types';
import {
  FavouritesSection,
  HoverNavRow,
  PanelBadge,
  PanelEmpty,
  PanelError,
  PanelLoading,
  PanelPill,
  PanelRowIcon,
  PanelSectionHeader,
  PanelToggle,
  SidebarSearchInput,
} from './primitives';
import type { FavouriteCandidate } from './favouritesContext';
import { PANEL_ROW_CLASS, favouriteId } from './rowStyles';
import { dotToneClass, iconToneClass, trafficTone } from './tones';
import { resolveIcon } from './icons';
import { getPath, getString, interpolate } from './interpolate';
import { evalPredicate, isRowActive, rowHref, type PanelCtx } from './context';
import { SidebarFilterPopover } from './SidebarFilterPopover';
import { SidebarTreeSection } from './SidebarTreeSection';
import { useSidebarData, type ResolvedData } from './useSidebarData';

/**
 * Named data providers injected by the gateway. Each is a hook returning
 * already-shaped rows for data a declarative { api, map } can't express (RBAC
 * joins, preference sorting, cross-field counts). A config still references a
 * provider by NAME only, so it stays pure JSON.
 */
export type SidebarProviderRegistry = Record<string, () => SidebarProviderResult>;

/** Providers that produce whole SECTIONS, for panels whose groups are data. */
export type SidebarSectionProviderRegistry = Record<string, () => SidebarSectionsResult>;

interface DeclarativePanelProps {
  config: PanelConfig;
  onNavigate: () => void;
  providers?: SidebarProviderRegistry;
  sectionProviders?: SidebarSectionProviderRegistry;
}

/** Map API records into rows using a declarative RowMapping. */
function mapRows(
  payload: unknown,
  mapping: RowMapping,
  ctxBag: Record<string, string | number | null | undefined>,
): RowConfig[] {
  const list = mapping.listPath ? getPath(payload, mapping.listPath) : payload;
  const records = Array.isArray(list) ? list : [];
  return records.map((rec, i) => ({
    id: getString(rec, mapping.id) || String(i),
    label:
      getString(rec, mapping.label) ||
      (mapping.labelFallback ? getString(rec, mapping.labelFallback) : ''),
    icon: mapping.icon,
    to: mapping.to ? interpolate(mapping.to, rec, ctxBag) : undefined,
    code: mapping.badge ? getString(rec, mapping.badge) : undefined,
    dotColor: mapping.colorDotField
      ? getString(rec, mapping.colorDotField) || undefined
      : undefined,
    // A record with no colour still renders the dot, in the neutral tone.
    dotTone: mapping.colorDotField ? 'gray' : undefined,
  }));
}

/** Resolve a badge's display value + tone from config + fetched data. */
function resolveBadge(
  badge: BadgeConfig,
  resolved: ResolvedData,
  key: string,
): { value: number; tone: BadgeTone } | null {
  const readCount = (
    c: NonNullable<BadgeConfig['count']>,
    q: ResolvedData[string] | undefined,
  ): number => {
    if (c.lengthPath !== undefined) {
      const arr = c.lengthPath ? getPath(q?.data, c.lengthPath) : q?.data;
      return Array.isArray(arr) ? arr.length : 0;
    }
    const v = getPath(q?.data, c.valuePath);
    return typeof v === 'number' ? v : Number(v ?? 0);
  };

  let value = badge.value;
  if (badge.count) {
    const q = resolved[key];
    if (q?.isLoading || q?.isError) return null;
    value = readCount(badge.count, q);
  } else if (badge.sum) {
    let total = 0;
    for (let i = 0; i < badge.sum.length; i += 1) {
      const c = badge.sum[i];
      if (!c) continue;
      const q = resolved[`${key}:sum:${i}`];
      if (q?.isLoading || q?.isError) return null;
      total += readCount(c, q);
    }
    value = total;
  }
  if (value == null) return null;
  const tone =
    badge.kind === 'traffic-light' ? trafficTone(value, badge.thresholds) : (badge.tone ?? 'gray');
  return { value, tone };
}

export function DeclarativePanel({
  config,
  onNavigate,
  providers,
  sectionProviders,
}: DeclarativePanelProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canBase, canForProject, canAnyProject, loaded } = usePermission();
  const { permissions, loadedApps, featureFlags } = usePermissionsSlice();

  // An app that resolves its OWN matrix (`publishAppPermissions`) flips only its
  // per-app flag; the global `loaded` belongs to the projects matrix and never
  // turns true for it. Gating on the global flag alone therefore fails EVERY
  // predicate closed forever — an empty panel, not a permission decision. The
  // active app is the first path segment, the same rule the rail uses.
  const appSlug = pathname.split('/').filter(Boolean)[0] ?? '';
  const permissionsLoaded = loaded || loadedApps[appSlug] === true;

  const [query, setQuery] = useState('');

  // Section toggle-filter state (the "My projects" scope switch), seeded from
  // localStorage. The panel is keyed by panelId, so this init runs once.
  const [toggles, setToggles] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const sec of config.sections) {
      if (!sec.toggleFilter) continue;
      const stored =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(sec.toggleFilter.storageKey)
          : null;
      init[sec.id] = stored == null ? (sec.toggleFilter.defaultOn ?? false) : stored === 'true';
    }
    return init;
  });
  const setToggle = (secId: string, storageKey: string, next: boolean) => {
    setToggles((t) => ({ ...t, [secId]: next }));
    try {
      window.localStorage.setItem(storageKey, String(next));
    } catch {
      /* ignore storage write failures (private mode / quota) */
    }
  };

  const ctxBag = useMemo(() => {
    const bag: Record<string, string | number | null | undefined> = {};
    searchParams.forEach((v, k) => {
      bag[k] = v;
    });
    return bag;
  }, [searchParams]);

  const resolved = useSidebarData(config, ctxBag);

  // A panel whose GROUPS are data (CRM's master-config domains) resolves them
  // first; the sections it returns carry inline rows, so they never nest another
  // provider and the hook order below stays stable.
  const sectionsHook = config.sectionsSource
    ? sectionProviders?.[config.sectionsSource.provider]
    : undefined;
  const dynamic = sectionsHook?.();
  const sections = dynamic ? [...config.sections, ...dynamic.sections] : config.sections;

  // Named row providers (gateway-injected hooks), called in a stable order — the
  // panel is keyed by panelId, so config.sections never changes while mounted.
  const providerResults: Record<string, SidebarProviderResult> = {};
  for (const sec of config.sections) {
    const src = sec.rowsSource;
    const hook = src && 'provider' in src ? providers?.[src.provider] : undefined;
    if (hook) providerResults[sec.id] = hook();
  }

  const ctx: PanelCtx = {
    // Reads the merged map directly rather than usePermission().can, whose own
    // global-`loaded` short-circuit would re-introduce the bug above. Identical
    // once that flag is true. `canBase` / `canForProject` stay on the hook —
    // they read the projects-only base + per-project caches.
    can: (k) => permissions[k] === true,
    canBase: (k) => canBase(k as Parameters<typeof canBase>[0]),
    canForProject: (k, projectId) => canForProject(k as Parameters<typeof canBase>[0], projectId),
    canAnyProject: (k) => canAnyProject(k as Parameters<typeof canBase>[0]),
    flagOn: (code) => featureFlags[code] === true,
    permissionsLoaded,
    params: searchParams,
    pathname,
  };

  const q = query.trim().toLowerCase();
  const matchesQuery = (label: string) => !q || label.toLowerCase().includes(q);

  /**
   * A row survives the search box if its own text matches — OR if the query
   * matches the heading it sits under, because "show me the Compliance group"
   * is what a user typing "compliance" means. Restores the behaviour each
   * hand-written grouped panel implemented for itself.
   */
  const rowMatchesSearch = (row: RowConfig, sectionLabel?: string) => {
    if (!q) return true;
    if (sectionLabel?.toLowerCase().includes(q)) return true;
    if (matchesQuery(row.label)) return true;
    return (row.searchTerms ?? []).some((t) => t.toLowerCase().includes(q));
  };

  /** Rows for a section, whatever their source. */
  const dataFor = (
    sec: SectionConfig,
  ): { rows: RowConfig[]; isLoading: boolean; isError: boolean; emptyText?: string } => {
    const src = sec.rowsSource;
    if (src && 'provider' in src) {
      const r = providerResults[sec.id];
      return {
        rows: r?.rows ?? [],
        isLoading: r?.isLoading ?? false,
        isError: r?.isError ?? false,
        emptyText: r?.emptyText,
      };
    }
    if (src && 'api' in src) {
      const q = resolved[`sec:${sec.id}:rows`];
      return {
        rows: q?.data ? mapRows(q.data, src.map, ctxBag) : [],
        isLoading: q?.isLoading ?? false,
        isError: q?.isError ?? false,
      };
    }
    return { rows: sec.rows ?? [], isLoading: false, isError: false };
  };

  /** Does this section still show anything after RBAC + the search box? */
  const hasVisibleRows = (sec: SectionConfig) =>
    dataFor(sec)
      .rows.filter((r) => evalPredicate(r.visibleWhen, { ...ctx, scopeId: r.id }))
      .some((r) => rowMatchesSearch(r, sec.label));

  // "The first visible param-row is the default tab" (Reports, Settings) —
  // opt-in per panel, so a panel whose rows only select a view (Calendar) shows
  // nothing active until the user picks one.
  if (config.defaultActiveFirst) {
    const firstByGroup = new Map<string, string>();
    const consider = (row: RowConfig) => {
      if (!row.toParam) return;
      if (!evalPredicate(row.visibleWhen, { ...ctx, scopeId: row.id })) return;
      if (!matchesQuery(row.label)) return;
      const groupKey = `${row.toParam.pathname}|${row.toParam.param}`;
      if (!firstByGroup.has(groupKey)) firstByGroup.set(groupKey, row.id);
    };
    for (const row of config.headerRows ?? []) consider(row);
    for (const sec of sections) {
      if (!evalPredicate(sec.visibleWhen, ctx)) continue;
      for (const r of dataFor(sec).rows) consider(r);
    }
    ctx.defaultActiveIds = new Set(firstByGroup.values());
  }

  // Everything this user can reach from the panel right now, independent of the
  // search box and of which tree node is expanded — what a favourite resolves to.
  const favouriteCandidates: FavouriteCandidate[] = [];
  const addCandidate = (row: RowConfig, id: string, label: string, scopeId = row.id) => {
    if (row.emphasis === 'cta' || row.subtitle != null) return;
    if (!evalPredicate(row.visibleWhen, { ...ctx, scopeId })) return;
    const to = rowHref(row, ctx);
    if (to) favouriteCandidates.push({ id, label, to, active: isRowActive(row, ctx) });
  };
  for (const row of config.headerRows ?? [])
    addCandidate(row, favouriteId('header', row.id), row.label);
  for (const sec of sections) {
    if (!evalPredicate(sec.visibleWhen, ctx)) continue;
    if (sec.tree) {
      // Tree parents are entities (pinned, not starred); their section tabs are the menu.
      for (const parent of providerResults[sec.id]?.rows ?? []) {
        for (const tab of parent.expandable?.rows ?? []) {
          addCandidate(
            tab,
            favouriteId(sec.id, parent.id, tab.id),
            `${parent.label} › ${tab.label}`,
            parent.id,
          );
        }
      }
      continue;
    }
    for (const row of dataFor(sec).rows) addCandidate(row, favouriteId(sec.id, row.id), row.label);
  }

  const renderRow = (row: RowConfig, sectionId: string) => {
    if (!evalPredicate(row.visibleWhen, { ...ctx, scopeId: row.id })) return null;
    if (!matchesQuery(row.label)) return null;
    const href = rowHref(row, ctx);
    if (!href) return null;

    const active = isRowActive(row, ctx);
    const badge = row.badge
      ? resolveBadge(row.badge, resolved, `row:${sectionId}:${row.id}:badge`)
      : null;
    // A raw runtime colour can't be a class — the one case UI rule 1 allows an
    // inline style (a value that only exists at runtime).
    const dotStyle: CSSProperties | undefined = row.dotColor
      ? { backgroundColor: row.dotColor }
      : undefined;
    const Icon = resolveIcon(row.icon);

    // Call-to-action row (Schedule Meeting) — indigo text, never active.
    if (row.emphasis === 'cta') {
      return (
        <button
          key={row.id}
          type="button"
          onClick={() => {
            navigate(href);
            onNavigate();
          }}
          className={cn(PANEL_ROW_CLASS, 'text-indigo-600 hover:bg-indigo-50')}
        >
          <Plus className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
          <span className="font-medium">{row.label}</span>
        </button>
      );
    }

    // Two-line row (meetings): a colour dot only when the row asks for one, plus an optional chevron.
    if (row.subtitle != null) {
      return (
        <button
          key={row.id}
          type="button"
          onClick={() => {
            navigate(href);
            onNavigate();
          }}
          className={cn(
            'group',
            PANEL_ROW_CLASS,
            'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
          )}
        >
          {(row.dotColor || row.dotTone) && (
            <span
              className={cn(
                'h-2 w-2 flex-shrink-0 rounded-full',
                !row.dotColor && dotToneClass(row.dotTone ?? 'gray'),
              )}
              style={dotStyle}
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-medium">{row.label}</p>
            <p className="text-muted-foreground text-sm">{row.subtitle}</p>
          </div>
          {row.trailingChevron && (
            <ChevronRight
              className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 transition-colors group-hover:text-gray-500"
              strokeWidth={1.5}
            />
          )}
        </button>
      );
    }

    return (
      <HoverNavRow
        key={row.id}
        to={href}
        onNavigate={onNavigate}
        active={active}
        activeVariant={row.activeVariant}
        favourite={{ id: favouriteId(sectionId, row.id), label: row.label }}
      >
        {row.dynamicIcon ? (
          <DynamicIcon
            name={row.dynamicIcon.name || 'Folder'}
            className="h-4 w-4 flex-shrink-0"
            style={row.dynamicIcon.color ? { color: row.dynamicIcon.color } : undefined}
          />
        ) : row.dotColor || row.dotTone ? (
          <span
            className={cn(
              'h-2 w-2 flex-shrink-0 rounded-full',
              !row.dotColor && dotToneClass(row.dotTone ?? 'gray'),
            )}
            style={dotStyle}
          />
        ) : Icon ? (
          // A fixed tone means the icon carries its own meaning (Active users
          // are emerald whether or not the row is selected), so it opts out of
          // the row's active-chrome treatment.
          <PanelRowIcon
            icon={Icon}
            active={row.iconTone ? false : undefined}
            className={row.iconTone ? iconToneClass(row.iconTone) : undefined}
          />
        ) : null}
        {/* One place for every JSON-driven panel's row label, so marking the
            matched term here gives all of them the highlight at once. */}
        <HighlightText
          text={row.label}
          query={query}
          className={cn('min-w-0 flex-1', (row.code || row.pills) && 'truncate')}
        />
        {row.externalLinkIcon && (
          <ExternalLink
            className={cn(
              'h-3 w-3 flex-shrink-0',
              // `strong` sits 4px further in so its centre shares the vertical
              // axis of the panel header's collapse chevron.
              row.externalLinkIcon === 'strong' ? 'mr-1 text-gray-800' : 'text-gray-300',
            )}
            strokeWidth={1.5}
          />
        )}
        {row.pinned && (
          <Pin
            className="h-2.5 w-2.5 flex-shrink-0 rotate-45 fill-indigo-500 text-indigo-500"
            strokeWidth={1.5}
          />
        )}
        {row.pills?.map((p) => (
          <PanelPill key={p.label} label={p.label} className={p.className} title={p.title} />
        ))}
        {row.code && (
          <span className="text-muted-foreground text-xxs flex-shrink-0">{row.code}</span>
        )}
        {badge && <PanelBadge kind={row.badge!.kind} value={badge.value} tone={badge.tone} />}
      </HoverNavRow>
    );
  };

  const renderSection = (sec: SectionConfig) => {
    if (!evalPredicate(sec.visibleWhen, ctx)) return null;

    // Provider-backed drag-reorderable entity tree (owns its own header).
    if (sec.tree) {
      const result = providerResults[sec.id];
      if (!result) return null;
      return (
        <SidebarTreeSection
          key={sec.id}
          sec={sec}
          result={result}
          ctx={ctx}
          onNavigate={onNavigate}
          matchLabel={matchesQuery}
          hasQuery={Boolean(query.trim())}
        />
      );
    }

    const data = dataFor(sec);
    // Three tiers, and the difference matters: RBAC decides whether a row
    // EXISTS, the search box decides whether the SECTION is worth showing at
    // all, and the scope toggle only narrows what's listed under a header that
    // stays put (so "N of them are hidden" still reads as a filter, not as an
    // empty panel).
    const permittedRows = data.rows.filter((r) =>
      evalPredicate(r.visibleWhen, { ...ctx, scopeId: r.id }),
    );
    const searchedRows = permittedRows.filter((r) => rowMatchesSearch(r, sec.label));
    const toggle = sec.toggleFilter;
    const toggleVisible = toggle ? evalPredicate(toggle.visibleWhen, ctx) : false;
    // Without the toggle rendered the user can't switch scope, so the filter is
    // forced ON — mirrors "no view-all permission ⇒ always only your own".
    const toggleOn = toggle ? (toggleVisible ? Boolean(toggles[sec.id]) : true) : false;
    const visibleRows =
      toggle && toggleOn
        ? searchedRows.filter((r) => Boolean(r.flags?.[toggle.field]))
        : searchedRows;

    // "No matching teams" is a different message from "you're not in any team
    // yet" — the first tells the user to clear their search, the second doesn't.
    const emptyText =
      query.trim() && sec.searchEmptyText ? sec.searchEmptyText : (data.emptyText ?? sec.emptyText);

    // Collapse a section with nothing to show (RBAC-hidden statics, an empty
    // "Recent" list, a search that excludes it) unless it declares empty copy
    // or is still resolving.
    if (!data.isLoading && !data.isError && searchedRows.length === 0 && !emptyText) return null;

    // The header appears once the section has rows, for sections whose divider
    // would otherwise float above an empty space.
    const showHeader = !sec.hideHeaderUntilData || searchedRows.length > 0;
    const labelText =
      sec.label && sec.labelCountSuffix && !data.isLoading
        ? `${sec.label} · ${visibleRows.length} ${sec.labelCountSuffix}`
        : sec.label;
    const headerControl = sec.filterPopover ? (
      <SidebarFilterPopover config={sec.filterPopover} pathname={pathname} />
    ) : toggle && toggleVisible ? (
      <PanelToggle
        label={toggle.label}
        ariaLabel={toggle.ariaLabel}
        checked={toggleOn}
        onChange={(next) => setToggle(sec.id, toggle.storageKey, next)}
      />
    ) : null;

    // Groups are separated by whitespace, never a rule: a labelled, `divider` or
    // `spaced` section opens a new group; an unlabelled one continues the last.
    const startsGroup =
      showHeader && Boolean(labelText || headerControl || sec.divider || sec.spaced);

    return (
      <div key={sec.id} className={cn(startsGroup && 'mt-4 first:mt-0')}>
        {showHeader && <PanelSectionHeader label={labelText} control={headerControl} />}
        {data.isLoading && sec.loadingVariant === 'skeleton' && (
          <div className="space-y-1.5 px-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 rounded-lg" />
            ))}
          </div>
        )}
        {data.isLoading && sec.loadingVariant === 'text' && <PanelEmpty>Loading…</PanelEmpty>}
        {data.isLoading && !sec.loadingVariant && <PanelLoading />}
        {!data.isLoading && data.isError && (
          <PanelError>{sec.errorText ?? 'Failed to load'}</PanelError>
        )}
        {!data.isLoading && !data.isError && (
          <div className="mb-1 space-y-0.5 px-2">
            {visibleRows.map((r) => renderRow(r, sec.id))}
            {visibleRows.length === 0 && emptyText && <PanelEmpty>{emptyText}</PanelEmpty>}
          </div>
        )}
      </div>
    );
  };

  const headerRows = (config.headerRows ?? [])
    .map((row) => renderRow(row, 'header'))
    .filter(Boolean);

  return (
    <div className="py-2">
      {/* Search leads the panel; the page links sit directly under it as its primary exits. */}
      {config.search && (
        <SidebarSearchInput
          value={query}
          onChange={setQuery}
          placeholder={config.search.placeholder}
        />
      )}
      {headerRows.length > 0 && (
        <div className={cn('mb-1.5 space-y-0.5 px-2', config.search && 'mt-2')}>{headerRows}</div>
      )}
      <FavouritesSection candidates={favouriteCandidates} onNavigate={onNavigate} query={query} />
      {sections.map(renderSection)}
      {/* Skeleton only while the dynamic groups resolve — a panel whose static
          sections already rendered shows those immediately above it. */}
      {dynamic?.isLoading && dynamic.loadingVariant === 'skeleton' && (
        <div className="space-y-1 px-2 py-1">
          {['a', 'b', 'c', 'd', 'e', 'f'].map((id) => (
            <Skeleton key={id} className="h-7 w-full rounded-lg" />
          ))}
        </div>
      )}
      {!dynamic?.isLoading && dynamic?.emptyText && sections.every((s) => !hasVisibleRows(s)) && (
        <PanelEmpty>{dynamic.emptyText}</PanelEmpty>
      )}
    </div>
  );
}
